import { useEffect, useState } from "react";
import { useRoomSocket } from "./socket/useRoomSocket.js";
import { SyncPlayer } from "./player/SyncPlayer.js";
import { SourcePanel } from "./source/SourcePanel.js";
import { ChatPanel } from "./chat/ChatPanel.js";
import { VirtualBrowserPanel } from "./browser/VirtualBrowserPanel.js";
import { NameSettings } from "./settings/NameSettings.js";
import { loadOrCreateName } from "./lib/displayName.js";

export function App(): JSX.Element {
  const { state, actions, syncDirective, onScreenFrame } = useRoomSocket();
  const [selfClientId, setSelfClientId] = useState<string>(() => readClientId());
  const [name, setName] = useState<string>(() => loadOrCreateName());

  // The socket hook joins with a freshly generated clientId each connect.
  // For "self" comparison, use the latest known clientId from the WS layer
  // — we don't expose it directly. For now we just label messages by name.
  useEffect(() => {
    // Attempt to read the clientId assigned by the welcome. Since we
    // didn't expose it on the hook, fall back to localStorage.
    try {
      const id = localStorage.getItem("synthetic-thunder.clientId");
      if (id) setSelfClientId(id);
    } catch {
      // ignore
    }
  }, [state.connected]);

  const closeBrowser = () => {
    actions.browserClose();
  };

  return (
    <div className="app">
      <header className="header">
        <h1>Synthetic Thunder</h1>
        <div className="connection">
          <span className={`dot ${state.connected ? "ok" : "down"}`} />
          <span>{state.connected ? "Connected" : "Disconnected"}</span>
        </div>
        <NameSettings
          name={name}
          participants={state.participants}
          selfClientId={selfClientId}
          onSave={(newName) => {
            setName(newName);
            actions.setName(newName);
          }}
        />
      </header>
      <main className="main">
        <div className="left">
          <SyncPlayer
            playback={state.playback}
            appliedRevision={state.appliedRevision}
            syncDirective={syncDirective}
            onPlay={actions.play}
            onPause={actions.pause}
            onSeek={actions.seek}
            sendSyncReport={actions.sendSyncReport}
          />
        </div>
        <div className="right">
          <SourcePanel state={state} actions={actions} />
          <ChatPanel messages={state.chat} selfClientId={selfClientId} onSend={actions.sendChat} />
        </div>
      </main>
      <VirtualBrowserPanel
        state={state}
        actions={actions}
        onScreenFrame={onScreenFrame}
        onClose={closeBrowser}
      />
    </div>
  );
}

function readClientId(): string {
  try {
    const id = localStorage.getItem("synthetic-thunder.clientId");
    if (id) return id;
    // Generate and persist.
    const fresh = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("synthetic-thunder.clientId", fresh);
    return fresh;
  } catch {
    return "";
  }
}